/**
 * EmailTemplateSelector Component
 * Provides template selection with preview and placeholder customization
 */

import { useState, useMemo } from 'react';
import { Search, Eye, Send, Copy, Check } from 'lucide-react';
import { cn, formatCurrency, formatPercentage } from '@/lib/utils';
import type {
  EmailTemplate,
  TemplateCategoryType,
  TemplatePlaceholderMapping,
} from '@/types/negotiation';
import {
  templateCategoryLabels,
  TemplateCategory,
} from '@/types/negotiation';
import type { Deal, DealWithFund } from '@/types/database';

// ============================================================================
// DEFAULT TEMPLATES
// ============================================================================

const DEFAULT_TEMPLATES: EmailTemplate[] = [
  {
    id: '1',
    name: 'Initial Outreach - Direct LP',
    category: TemplateCategory.INITIAL_OUTREACH,
    subject: 'Interest in {{fund_name}} LP Position',
    body: `Hi {{seller_first_name}},

I hope this email finds you well. I'm {{your_name}}, {{your_title}} at {{your_company}}, and we specialize in secondary market transactions for venture capital LP interests.

We're currently interested in acquiring LP positions in {{fund_name}} and believe there may be an opportunity for a mutually beneficial transaction.

Would you be open to a brief conversation to explore this? We're prepared to move quickly and can offer competitive pricing with a streamlined process.

Looking forward to hearing from you.

Best regards,
{{your_name}}`,
    placeholders: ['{{fund_name}}', '{{seller_first_name}}', '{{your_name}}', '{{your_title}}', '{{your_company}}'],
    suggested_delay_hours: 0,
    tags: ['outreach', 'lp', 'initial'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '2',
    name: 'Follow-Up - No Response',
    category: TemplateCategory.FOLLOW_UP,
    subject: 'Re: Interest in {{fund_name}} LP Position',
    body: `Hi {{seller_first_name}},

I wanted to follow up on my previous email regarding your {{fund_name}} LP position.

I understand you may be busy, so I'll keep this brief. We're actively seeking positions in quality funds like {{fund_name}} and would value the opportunity to discuss this with you.

If the timing isn't right, I completely understand. Just let me know, and we can reconnect when it makes more sense.

Thanks for your consideration.

Best,
{{your_name}}`,
    placeholders: ['{{fund_name}}', '{{seller_first_name}}', '{{your_name}}'],
    suggested_delay_hours: 96,
    tags: ['follow-up', 'gentle'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '3',
    name: 'Counter Offer - Price Negotiation',
    category: TemplateCategory.COUNTER_OFFER,
    subject: 'Revised Proposal for {{deal_name}}',
    body: `Hi {{seller_first_name}},

Thank you for your feedback on our initial proposal for {{deal_name}}.

After careful consideration and additional analysis, we'd like to propose a revised structure:

• **Purchase Price**: {{target_value}}
• **Discount to NAV**: {{headline_discount}}
• **Estimated Close**: {{estimated_close_date}}

This pricing reflects our assessment of the fund's performance, remaining term, and current market conditions for secondaries. We believe this represents a fair valuation that accounts for both parties' interests.

We're ready to move forward quickly with due diligence and documentation if this works for you. Would you be available for a call this week to discuss?

Best regards,
{{your_name}}`,
    placeholders: ['{{deal_name}}', '{{seller_first_name}}', '{{target_value}}', '{{headline_discount}}', '{{estimated_close_date}}', '{{your_name}}'],
    suggested_delay_hours: 24,
    tags: ['counter-offer', 'pricing', 'negotiation'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '4',
    name: 'Acceptance - Move to Due Diligence',
    category: TemplateCategory.ACCEPTANCE,
    subject: 'Next Steps: Due Diligence for {{deal_name}}',
    body: `Hi {{seller_first_name}},

Great news! We're excited to move forward with the {{deal_name}} transaction at the terms we discussed.

**Next Steps:**
1. We'll send over our standard NDA and due diligence request list today
2. Once we receive the materials, our review typically takes 5-7 business days
3. Assuming everything checks out, we can move to documentation immediately

**Timeline:**
• Due Diligence: 1 week
• Documentation: 1-2 weeks
• GP Consent (if required): 2-4 weeks
• **Target Close**: {{estimated_close_date}}

Please let me know if you have any questions. I'll send the NDA and diligence list separately.

Looking forward to working together on this.

Best,
{{your_name}}`,
    placeholders: ['{{deal_name}}', '{{seller_first_name}}', '{{estimated_close_date}}', '{{your_name}}'],
    suggested_delay_hours: 0,
    tags: ['acceptance', 'due-diligence', 'next-steps'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '5',
    name: 'GP Introduction - Consent Process',
    category: TemplateCategory.GP_INTRODUCTION,
    subject: 'Introduction: Secondary Transaction for {{fund_name}}',
    body: `Hi [GP Name],

I hope this email finds you well. I'm {{your_name}} from {{your_company}}.

We're working with {{seller_name}}, an LP in {{fund_name}}, on a potential secondary transaction for their LP interest. Per the fund's LPA, we're reaching out to request transfer consent and ensure you have the opportunity to exercise any ROFR rights.

**Transaction Overview:**
• Seller: {{seller_name}}
• Fund: {{fund_name}}
• Proposed Transfer Date: {{estimated_close_date}}

We'd be happy to provide additional details and documentation as needed. Could you please confirm the formal consent process and timeline?

We're committed to a smooth process and want to ensure we follow all required procedures.

Best regards,
{{your_name}}
{{your_company}}`,
    placeholders: ['{{fund_name}}', '{{seller_name}}', '{{estimated_close_date}}', '{{your_name}}', '{{your_company}}'],
    suggested_delay_hours: 0,
    tags: ['gp', 'consent', 'introduction'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '6',
    name: 'Due Diligence - Information Request',
    category: TemplateCategory.DUE_DILIGENCE,
    subject: 'Due Diligence Request for {{deal_name}}',
    body: `Hi {{seller_first_name}},

As discussed, we're ready to begin our due diligence review for {{deal_name}}.

**Requested Materials:**
1. Most recent fund statements (last 4 quarters)
2. Capital account statement
3. LPA and all amendments
4. Distribution and capital call history
5. Any correspondence with the GP regarding ROFR/transfer rights

Please upload these to our secure data room (link to follow separately) or send via encrypted email.

Once we have these materials, we can typically complete our review within 5-7 business days. Please let me know if you have any questions or if any items will take longer to gather.

Thanks for your cooperation.

Best,
{{your_name}}`,
    placeholders: ['{{deal_name}}', '{{seller_first_name}}', '{{your_name}}'],
    suggested_delay_hours: 0,
    tags: ['due-diligence', 'information-request'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '7',
    name: 'Closing Coordination - Final Steps',
    category: TemplateCategory.CLOSING_COORDINATION,
    subject: 'Final Steps to Close {{deal_name}}',
    body: `Hi {{seller_first_name}},

We're in the home stretch! Here's what we need to finalize the {{deal_name}} transaction:

**Outstanding Items:**
☐ Signed purchase agreement (sent for signature yesterday)
☐ Executed GP consent letter (pending from {{gp_name}})
☐ Wire instructions for closing payment
☐ Final updated capital account statement

**Closing Timeline:**
• Target Close Date: {{estimated_close_date}}
• Wire Transfer: Same-day upon receipt of all signed documents
• GP Notification: We'll handle directly after closing

Please confirm receipt of the purchase agreement and let me know if you have any final questions.

We appreciate your partnership throughout this process.

Best regards,
{{your_name}}`,
    placeholders: ['{{deal_name}}', '{{seller_first_name}}', '{{gp_name}}', '{{estimated_close_date}}', '{{your_name}}'],
    suggested_delay_hours: 0,
    tags: ['closing', 'final-steps', 'coordination'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface EmailTemplateSelectorProps {
  deal: Deal | DealWithFund;
  onTemplateSelect: (template: EmailTemplate, filledContent: { subject: string; body: string }) => void;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function EmailTemplateSelector({
  deal,
  onTemplateSelect,
  className,
}: EmailTemplateSelectorProps): React.ReactElement {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategoryType | 'all'>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [customPlaceholders, setCustomPlaceholders] = useState<Partial<TemplatePlaceholderMapping>>({
    your_name: 'Ed Dua',
    your_title: 'Managing Partner',
    your_company: 'Secondary Market Fund',
  });
  const [copied, setCopied] = useState(false);

  // Build placeholder mapping from deal data
  const placeholderMapping = useMemo((): TemplatePlaceholderMapping => {
    const sellerFirstName = deal.seller_name.split(' ')[0] ?? deal.seller_name;
    const dealWithFund = deal as DealWithFund;
    const fundName = dealWithFund.fund?.name ?? 'the fund';
    const gpName = dealWithFund.fund?.gp_name ?? 'the GP';

    return {
      deal_name: deal.name,
      seller_name: deal.seller_name,
      seller_first_name: sellerFirstName,
      fund_name: fundName,
      gp_name: gpName,
      target_value: formatCurrency(deal.target_value),
      headline_discount: deal.headline_discount ? formatPercentage(deal.headline_discount) : 'TBD',
      true_discount: deal.true_discount ? formatPercentage(deal.true_discount) : 'TBD',
      estimated_close_date: deal.estimated_close_date ?? 'TBD',
      your_name: customPlaceholders.your_name ?? 'Ed Dua',
      your_title: customPlaceholders.your_title ?? 'Managing Partner',
      your_company: customPlaceholders.your_company ?? 'Secondary Market Fund',
    };
  }, [deal, customPlaceholders]);

  // Fill template with actual values
  const fillTemplate = (content: string): string => {
    let filled = content;
    Object.entries(placeholderMapping).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      const regex = new RegExp(placeholder.replace(/[{()}]/g, '\\$&'), 'g');
      filled = filled.replace(regex, value);
    });
    return filled;
  };

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return DEFAULT_TEMPLATES.filter((template) => {
      const matchesSearch =
        searchQuery === '' ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory =
        selectedCategory === 'all' || template.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const handleTemplateClick = (template: EmailTemplate): void => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const handleUseTemplate = (): void => {
    if (!selectedTemplate) return;

    const filledContent = {
      subject: fillTemplate(selectedTemplate.subject),
      body: fillTemplate(selectedTemplate.body),
    };

    onTemplateSelect(selectedTemplate, filledContent);
  };

  const handleCopyToClipboard = async (): Promise<void> => {
    if (!selectedTemplate) return;

    const filledContent = `Subject: ${fillTemplate(selectedTemplate.subject)}\n\n${fillTemplate(selectedTemplate.body)}`;

    await navigator.clipboard.writeText(filledContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search and Filter */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value as TemplateCategoryType | 'all')}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          {Object.entries(templateCategoryLabels).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Template List */}
      <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
        {filteredTemplates.map((template) => (
          <button
            key={template.id}
            onClick={() => handleTemplateClick(template)}
            className={cn(
              'text-left p-4 border rounded-lg transition-all hover:shadow-md',
              selectedTemplate?.id === template.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{template.name}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  {templateCategoryLabels[template.category]}
                </p>
                <div className="flex gap-2 mt-2">
                  {template.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <Eye className="h-5 w-5 text-gray-400" />
            </div>
          </button>
        ))}

        {filteredTemplates.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No templates found matching your criteria
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedTemplate.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {templateCategoryLabels[selectedTemplate.category]}
                  </p>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Subject
                </label>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-gray-900">{fillTemplate(selectedTemplate.subject)}</p>
                </div>
              </div>

              {/* Body */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Body
                </label>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                  <pre className="whitespace-pre-wrap font-sans text-gray-900">
                    {fillTemplate(selectedTemplate.body)}
                  </pre>
                </div>
              </div>

              {/* Placeholder Values */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Customize Placeholders
                </label>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Your Name</label>
                      <input
                        type="text"
                        value={customPlaceholders.your_name ?? ''}
                        onChange={(e) =>
                          setCustomPlaceholders((prev) => ({
                            ...prev,
                            your_name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Your Title</label>
                      <input
                        type="text"
                        value={customPlaceholders.your_title ?? ''}
                        onChange={(e) =>
                          setCustomPlaceholders((prev) => ({
                            ...prev,
                            your_title: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-gray-200 flex justify-between">
              <button
                onClick={handleCopyToClipboard}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy to Clipboard
                  </>
                )}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUseTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Use Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
